import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useAuthStore } from '@/store/auth.store';
import { googleAuthService } from '@/services/googleAuth.service';
import type { AuthUser, V2LoginResponse, UserProfile, UserAuthority } from '@/types/auth.types';

interface AuthContextType {
  user: AuthUser | null;
  v2User: V2LoginResponse | null;
  userProfile: UserProfile | null;
  authority: UserAuthority | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, v2User, userProfile, authority, isAuthenticated, isLoading } = useAuthStore();

  useEffect(() => {
    return googleAuthService.observe();
  }, []);

  const value = useMemo(
    () => ({ user, v2User, userProfile, authority, isAuthenticated, isLoading }),
    [user, v2User, userProfile, authority, isAuthenticated, isLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Kept together to preserve the existing context API used throughout the app.
// eslint-disable-next-line react-refresh/only-export-components
export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
